import type { Adapter, AdapterConfig } from "./adapter";
import { LineProtocolBinding, registerAdapter } from "./adapter";
import type { ConnectionState } from "../core/messages";
import { LineSplitter } from "../core/codec";
import { timers } from "../core/timers";

/**
 * WebSerial adapter. Line-oriented; requires a user gesture to connect.
 * Reads are stream-driven so data keeps flowing in backgrounded tabs.
 */
export class SerialAdapter implements Adapter {
  state: ConnectionState = "idle";
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private reading: Promise<void> | null = null;
  private writing: Promise<void> = Promise.resolve();
  private opening: Promise<void> | null = null;
  private shuttingDown: Promise<void> | null = null;
  private generation = 0;
  private binding: LineProtocolBinding;
  private closing = false;
  private handshakeTimer: number | null = null;

  constructor(private config: AdapterConfig) {
    this.binding = new LineProtocolBinding(config, (line) => this.writeLine(line));
  }

  async connect(): Promise<void> {
    if (this.opening) return this.opening;
    if (this.state === "connected" && !this.shuttingDown) return;
    if (!("serial" in navigator)) {
      this.setState("error", "WebSerial is not available in this browser.");
      return;
    }
    const generation = ++this.generation;
    this.setState("connecting");
    const opening = this.openConnection(generation);
    this.opening = opening;
    try {
      await opening;
    } finally {
      if (this.opening === opening) this.opening = null;
    }
  }

  private async openConnection(generation: number): Promise<void> {
    let ownedPort: SerialPort | null = null;
    try {
      const signals: SerialOutputSignals = {};
      for (const [option, signal] of [["dtr", "dataTerminalReady"], ["rts", "requestToSend"]] as const) {
        const raw = this.config.options[option];
        if (raw === undefined) continue;
        if (raw !== "true" && raw !== "false") throw new Error(`Serial ${option} must be "true" or "false".`);
        signals[signal] = raw === "true";
      }
      const filters = this.parseFilters();
      // Keep the picker inside the click's activation, before cleanup awaits.
      const [port] = await Promise.all([
        navigator.serial.requestPort(filters ? { filters } : undefined),
        this.shuttingDown,
      ]);
      if (generation !== this.generation) return;
      // A cancelled WebSerial read can replace its stream. Keep the previous
      // loop stopped until shutdown releases it, even when reconnect overlaps.
      this.closing = false;
      const baudRate = Number(this.config.options.baud ?? "115200");
      await port.open({ baudRate });
      ownedPort = port;
      this.port = port;
      if (generation !== this.generation) return;
      // Explicit page preferences only: no board inference or reset sequence.
      if (Object.keys(signals).length) await port.setSignals(signals);
      if (generation !== this.generation) return;
      this.writer = port.writable?.getWriter() ?? null;
      this.binding.attach();
      this.setState("connected");
      if (generation !== this.generation) return;

      // DESCRIBE handshake — legacy firmware that never answers is fine.
      this.binding.requestDescriptor();
      this.handshakeTimer = timers.setTimeout(() => {
        this.handshakeTimer = null;
        this.config.onDescriptor?.(null);
      }, 2000);
      this.reading = this.readLoop(port);
    } catch (err) {
      if (ownedPort) await this.releaseSession();
      if (generation !== this.generation) return;
      const message = err instanceof Error ? err.message : String(err);
      const hint = /failed to open|already open|busy/i.test(message)
        ? " Close the IDE serial monitor, Arduino IDE and other tabs using this port, then reconnect."
        : "";
      this.setState("idle", message + hint);
    }
  }

  async disconnect(): Promise<void> {
    this.generation++;
    this.closing = true;
    this.binding.detach();
    this.clearHandshake();
    // A later disconnect also cancels a reconnect waiting on this shutdown.
    if (this.shuttingDown) return this.shuttingDown;
    const opening = this.opening;
    const shutdown = (async () => {
      try { await opening; } catch {}
      await this.releaseSession();
      this.setState("disconnected");
    })();
    this.shuttingDown = shutdown;
    try {
      await shutdown;
    } finally {
      if (this.shuttingDown === shutdown) this.shuttingDown = null;
    }
  }

  private async releaseSession(): Promise<void> {
    this.binding.detach();
    this.clearHandshake();
    const reader = this.reader;
    try { await reader?.cancel(); } catch {}
    try { await this.reading; } catch {}
    this.reading = null;
    this.reader = null;
    try { await this.writing; } catch {}
    try { this.writer?.releaseLock(); } catch {}
    this.writer = null;
    this.writing = Promise.resolve();
    const port = this.port;
    this.port = null;
    try { await port?.close(); } catch {
      // USB may already be gone. Every stream lock was released first.
    }
  }

  private clearHandshake(): void {
    if (this.handshakeTimer !== null) timers.clear(this.handshakeTimer);
    this.handshakeTimer = null;
  }

  private parseFilters(): SerialPortFilter[] | null {
    const raw = this.config.options.usbvendor;
    if (!raw) return null;
    const usbVendorId = Number(raw);
    return Number.isNaN(usbVendorId) ? null : [{ usbVendorId }];
  }

  private async readLoop(port: SerialPort): Promise<void> {
    const splitter = new LineSplitter();
    const decoder = new TextDecoder();
    while (this.port === port && port.readable && !this.closing) {
      const stream = port.readable;
      const reader = stream.getReader();
      this.reader = reader;
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!value) continue;
          for (const line of splitter.push(decoder.decode(value, { stream: true }))) {
            this.handleLine(line);
          }
        }
      } catch (err) {
        if (!this.closing) {
          this.setState("error", err instanceof Error ? err.message : String(err));
        }
      } finally {
        reader.releaseLock();
        if (this.reader === reader) this.reader = null;
      }
      // WebSerial may replace a stream after a recoverable read error. A
      // closed/errored stream that remains unchanged cannot be read again.
      if (port.readable === stream) break;
    }
    if (!this.closing && this.state === "connected") {
      this.setState("disconnected", "Serial port closed.");
    }
  }

  private handleLine(line: string): void {
    if (line.startsWith("DESCRIBE:") && this.handshakeTimer !== null) {
      timers.clear(this.handshakeTimer);
      this.handshakeTimer = null;
    }
    this.binding.handleLine(line);
  }

  private writeLine(line: string): void {
    const writer = this.writer;
    if (!writer || this.closing) return;
    const bytes = new TextEncoder().encode(line + "\n");
    this.writing = this.writing.then(() => writer.write(bytes)).catch(() => {});
  }

  private setState(state: ConnectionState, detail?: string): void {
    this.state = state;
    this.config.onState(state, detail);
  }
}

registerAdapter({
  name: "serial",
  fields: [
    { name: "baud", label: "Baud", placeholder: "115200", persist: true },
    { name: "usbvendor", label: "USB vendor ID", placeholder: "0x2341", persist: true },
    { name: "dtr", label: "DTR signal", placeholder: "true / false (optional)", persist: true },
    { name: "rts", label: "RTS signal", placeholder: "true / false (optional)", persist: true },
  ],
  requiresGesture: true,
  supported: () => typeof navigator !== "undefined" && "serial" in navigator,
  create: (config) => new SerialAdapter(config),
});
